/**
 * Groundwork Tasks v1 — header badge + tray (spec §Work Inbox).
 *
 * Badge = NEEDS YOU — open tasks assigned to this person and actionable now
 * (human tasks only; system queues like Office Review and Intake Desk keep
 * their own screens and counts). One named definition, computed server-side
 * and shared with the office toolbar and the Tasks page heading — see
 * `lib/counts.ts` and #1203. The tray shows the top items with one-click
 * open/done/snooze. The notification bell stays for other notifications; this
 * is the work count.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CalendarClock, Check, Inbox } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ageChip, type InboxItem, type InboxResponse } from "@/lib/tasks";
import { useFinishTask } from "@/components/hand-back-dialog";
import { openTaskHere } from "@/lib/task-flyout";

const TRAY_LIMIT = 8;

export function TaskInboxButton() {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const qc = useQueryClient();

    const inboxQ = useQuery<InboxResponse>({
        queryKey: ["task-inbox"],
        queryFn: () => api.get("/api/tasks/my-inbox"),
        refetchInterval: 30_000,
    });

    const handleClickOutside = useCallback((e: MouseEvent) => {
        if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }, []);

    useEffect(() => {
        if (open) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open, handleClickOutside]);

    const refresh = useCallback(() => {
        qc.invalidateQueries({ queryKey: ["task-inbox"] });
        qc.invalidateQueries({ queryKey: ["tasks"] });
    }, [qc]);

    // Done on a task someone else gave you hands it back; the hook asks for
    // the one line when the server needs it (#1645).
    const { finish, dialog: handBackDialog } = useFinishTask(refresh);

    const snoozeMut = useMutation({
        mutationFn: (taskId: string) => {
            const d = new Date();
            d.setDate(d.getDate() + 1);
            d.setHours(8, 0, 0, 0);
            return api.post(`/api/tasks/${taskId}/snooze`, { until: d.toISOString() });
        },
        onSuccess: () => {
            refresh();
            toast.success("Snoozed until tomorrow");
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
    });

    // The tray lists the rows; the badge is the server's NEEDS YOU number —
    // the same one the office toolbar and the Tasks page heading read. A badge
    // that counts the rows it happens to have fetched is the #1203 bug in
    // miniature, and this one was the fifth surface doing it.
    const myItems = (inboxQ.data?.items ?? []).filter((i) => !i.claimable);
    const count = inboxQ.data?.counts?.needsYou ?? myItems.length;

    function openItem(item: InboxItem) {
        setOpen(false);
        // Over the page you are on — a tech on a job stays on the job.
        openTaskHere(item.href, navigate);
    }

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen(!open)}
                className="relative rounded-md p-2 hover:bg-muted text-foreground/80 transition-colors"
                title="Tasks"
            >
                <Inbox className="h-5 w-5" />
                {count > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                        {count > 99 ? "99+" : count}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 top-full mt-2 w-96 max-h-[28rem] overflow-hidden rounded-xl border bg-card shadow-lg z-50 flex flex-col">
                    <div className="flex items-center justify-between px-4 py-3 border-b">
                        <h3 className="text-sm font-semibold">Tasks</h3>
                        <button
                            onClick={() => {
                                setOpen(false);
                                navigate("/inbox");
                            }}
                            className="text-xs text-primary hover:underline"
                        >
                            Open inbox
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto divide-y">
                        {myItems.length === 0 ? (
                            <div className="py-10 text-center text-sm text-foreground/70">
                                Nothing needs you right now
                            </div>
                        ) : (
                            myItems.slice(0, TRAY_LIMIT).map((item) => {
                                const chip = ageChip(item.createdAt);
                                return (
                                    <div
                                        key={`${item.kind}:${item.id}`}
                                        className="flex items-center gap-2 px-3 py-2.5 hover:bg-muted/50 transition-colors"
                                    >
                                        <button onClick={() => openItem(item)} className="flex-1 min-w-0 text-left">
                                            <div className="flex items-center gap-1.5">
                                                {item.priority === "urgent" && (
                                                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                                                )}
                                                <p className="truncate text-sm">{item.title}</p>
                                            </div>
                                            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-foreground/60">
                                                <span>{item.typeName ?? "Task"}</span>
                                                {chip && (
                                                    <span
                                                        className={cn(
                                                            "rounded-full px-1.5 py-px font-medium",
                                                            chip.amber ? "bg-amber-500/15 text-amber-600" : "bg-muted",
                                                        )}
                                                    >
                                                        {chip.label}
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                        <div className="flex shrink-0 gap-0.5">
                                            <button
                                                title="Done"
                                                onClick={() => finish({ id: item.id, title: item.title })}
                                                className="rounded p-1.5 hover:bg-emerald-500/15 text-foreground/60 hover:text-emerald-600 transition-colors"
                                            >
                                                <Check className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                title="Snooze until tomorrow"
                                                onClick={() => snoozeMut.mutate(item.id)}
                                                className="rounded p-1.5 hover:bg-muted text-foreground/60 transition-colors"
                                            >
                                                <CalendarClock className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                    {myItems.length > TRAY_LIMIT && (
                        <button
                            onClick={() => {
                                setOpen(false);
                                navigate("/inbox");
                            }}
                            className="border-t px-4 py-2 text-center text-xs text-primary hover:bg-muted/50"
                        >
                            {myItems.length - TRAY_LIMIT} more in the inbox
                        </button>
                    )}
                </div>
            )}
            {handBackDialog}
        </div>
    );
}
